import React, { useEffect, useState } from 'react';
import { fetchWorkshops, deleteWorkshop } from '../api'; 
import * as XLSX from 'xlsx';

const WorkshopsTable = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // --- FILTER STATES ---
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedState, setSelectedState] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setIsLoading(true);
    fetchWorkshops()
      .then(res => {
        setData(res.data);
        setIsLoading(false);
      })
      .catch(err => {
        console.error("Error fetching workshops:", err);
        setIsLoading(false);
      });
  };

  const handleDelete = async (id) => {
    const adminUser = window.prompt("Enter Super Admin Username:");
    if (adminUser === null) return; 
    if (!adminUser.trim()) {
      alert("Username is required");
      return;
    }

    const adminPass = window.prompt("Enter Super Admin Password:");
    if (adminPass === null) return; 
    if (!adminPass.trim()) {
      alert("Password is required");
      return;
    }

    if (window.confirm("Are you sure you want to delete this camp registration? This cannot be undone.")) {
      try {
        await deleteWorkshop(id, {
          headers: {
            "x-admin-user": adminUser.trim(),
            "x-admin-pass": adminPass
          }
        });
        
        setData(prevData => prevData.filter(item => item.id !== id));
        alert("Record successfully deleted manually by Super Admin.");
      } catch (err) {
        console.error("Failed to delete:", err);
        const errMsg = err.response?.data?.message || err.message;
        alert(`Failed to delete record: \n\n${errMsg}`);
      }
    }
  };

  // Extract unique states for the filter dropdown
  const uniqueStates = [...new Set(data.map(item => item.state))].filter(Boolean).sort();

  const resetFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSelectedState('');
  };

  // --- MULTI-CRITERIA FILTERING LOGIC ---
  const filteredData = data.filter(item => {
    // 1. Search Keyword Check
    const searchString = `
      ${item.visitorName} ${item.mobileNumber} ${item.registrationNo} 
      ${item.campName} ${item.district} ${item.clusterNgoName}
    `.toLowerCase();
    const matchesSearch = searchString.includes(searchTerm.toLowerCase());

    // 2. State Check
    const matchesState = selectedState === '' || item.state === selectedState;

    // 3. Date Range Check
    let matchesDate = true;
    if (startDate || endDate) {
      if (!item.submissionDate) {
        matchesDate = false; 
      } else {
        const itemDate = new Date(item.submissionDate);
        itemDate.setHours(0, 0, 0, 0); 

        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (itemDate < start) matchesDate = false;
        }
        
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999); 
          if (itemDate > end) matchesDate = false;
        }
      }
    }

    return matchesSearch && matchesState && matchesDate;
  });

  const handleExportExcel = () => {
    if (filteredData.length === 0) {
      alert("No data to export!");
      return;
    }

    const exportData = filteredData.map((item, index) => ({
      "S.No": index + 1,
      "Registration ID": item.registrationNo || "-",
      "Camp Name": item.campName || "-",
      "Camp Date": item.campDate ? new Date(item.campDate).toLocaleDateString() : "-",
      "Venue": item.venue || "-",
      "Visitor Name": item.visitorName || "-",
      "Father/Husband Name": item.relativeName || "-",
      "Age (Years)": item.age || "-",
      "Gender": item.gender || "-",
      "Marital Status": item.maritalStatus || "-",
      "Mobile Number": item.mobileNumber || "-",
      "Alt Contact": item.alternativeContact || "-",
      "Aadhaar Number": item.aadhaarNumber || "-",
      "Address": item.address || "-",
      "Village/City": item.villageCity || "-",
      "District": item.district || "-",
      "State": item.state || "-",
      "Category": item.category || "-",
      "Annual Family Income": item.annualFamilyIncome || "-",
      "Educational Qualification": item.educationalQualification || "-",
      "Present Occupation": item.presentOccupation || "-",
      "Present Monthly Income (₹)": item.presentMonthlyIncome ? `₹${item.presentMonthlyIncome}` : "-",
      "Purpose for Assistance": item.purposeForAssistance || "-",
      "Proposed Business": item.proposedBusiness || "-",
      "Approximate Loan Requirement (₹)": item.approximateLoanRequirement ? `₹${item.approximateLoanRequirement}` : "-",
      "Present Liability": item.presentLiability || "-",
      "Existing Liability": item.existingLiability || "-",
      "Belongs to Cluster/NGO/SHG": item.belongsToCluster || "-",
      "Cluster or SHG Name": item.clusterNgoName || "-",
      "Total Artisans inside Cluster": item.totalArtisans || "-",
      "Interested in NSFDC Fairs": item.interestedInExhibitions || "-",
      "Submission Date": item.submissionDate ? new Date(item.submissionDate).toLocaleString() : "-"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Registrations");
    XLSX.writeFile(workbook, "Awareness_Camp_Registrations.xlsx");
  };

  return (
    <div className="container-fluid mt-4 px-4 pb-5">
      
      {/* Top Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="text-black fw-bold mb-0">Awareness Camp Registrations</h4>
          <span className="badge bg-secondary fs-6">{filteredData.length} Records Found</span>
        </div>
        <button className="btn btn-sm btn-success d-flex align-items-center fw-bold shadow-sm" onClick={handleExportExcel} disabled={filteredData.length === 0}>
          <i className="bi bi-file-earmark-excel me-2"></i> Export Filtered to Excel
        </button>
      </div>

      {/* FILTER TOOLBAR */}
      <div className="bg-light p-3 rounded border mb-4 shadow-sm">
        <div className="row g-2 align-items-end">
          <div className="col-md-4">
            <label className="form-label small fw-bold text-muted mb-1">Search Keywords</label>
            <div className="input-group input-group-sm">
              <span className="input-group-text bg-white"><i className="bi bi-search text-muted"></i></span>
              <input 
                type="text" 
                className="form-control" 
                placeholder="Name, Mobile, Reg ID, Camp, Cluster..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="col-md-2">
            <label className="form-label small fw-bold text-muted mb-1">Filter by State</label>
            <select 
              className="form-select form-select-sm" 
              value={selectedState} 
              onChange={(e) => setSelectedState(e.target.value)}
            >
              <option value="">All States</option>
              {uniqueStates.map((state, idx) => (
                <option key={idx} value={state}>{state}</option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label small fw-bold text-muted mb-1">From Date</label>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label small fw-bold text-muted mb-1">To Date</label>
            <input 
              type="date" 
              className="form-control form-control-sm" 
              value={endDate}
              min={startDate} 
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="col-md-2 d-grid">
            <button className="btn btn-sm btn-outline-secondary" onClick={resetFilters}>
              <i className="bi bi-x-circle me-1"></i> Clear Filters
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 text-muted fw-bold">Loading Database...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredData.length > 0 ? (
            filteredData.map((item, index) => (
              <div key={item.id || index} className="card shadow-sm border border-gray-200 mb-4 rounded-3 overflow-hidden bg-white">
                
                {/* Card Top Bar */}
                <div className="card-header bg-[#0D1B5E] text-white d-flex flex-wrap justify-content-between align-items-center py-2 px-3">
                  <span className="fw-bold font-monospace text-warning">
                    #{index + 1} | Registration ID: {item.registrationNo || 'Pending'}
                  </span>
                  <span className="small text-white-50">
                    Submitted: {item.submissionDate ? new Date(item.submissionDate).toLocaleString() : '-'}
                  </span>
                </div>

                <div className="card-body p-3">
                  <div className="row g-3">
                    
                    {/* Column 1: Camp Details */}
                    <div className="col-md-3 border-end">
                      <h6 className="text-uppercase text-primary fw-bold small border-bottom pb-1 mb-2">Camp Details</h6>
                      <p className="mb-1 small"><strong>Camp:</strong> {item.campName || '-'}</p>
                      <p className="mb-1 small"><strong>Date:</strong> {item.campDate ? new Date(item.campDate).toLocaleDateString() : '-'}</p>
                      <p className="mb-0 small"><strong>Venue:</strong> {item.venue || '-'}</p>
                    </div>

                    {/* Column 2: Personal Details */}
                    <div className="col-md-3 border-end">
                      <h6 className="text-uppercase text-success fw-bold small border-bottom pb-1 mb-2">A. Personal Details</h6>
                      <p className="mb-1 small"><strong>Name:</strong> {item.visitorName || '-'}</p>
                      <p className="mb-1 small"><strong>Father's/Husband's:</strong> {item.relativeName || '-'}</p>
                      <p className="mb-1 small"><strong>Gender/Status:</strong> {item.gender || '-'} / {item.maritalStatus || '-'}</p>
                      <p className="mb-1 small"><strong>Age:</strong> {item.age ? `${item.age} Years` : '-'}</p>
                      <p className="mb-1 small"><strong>Mobile:</strong> <a href={`tel:${item.mobileNumber}`} className="text-decoration-none fw-bold">{item.mobileNumber || '-'}</a></p>
                      <p className="mb-1 small"><strong>Alt Contact:</strong> {item.alternativeContact || '-'}</p>
                      <p className="mb-1 small"><strong>Aadhaar:</strong> {item.aadhaarNumber || '-'}</p>
                      {/* CRITICAL FIX: Removed text-truncate to show the full address */}
                      <p className="mb-0 small">
                        <strong>Address:</strong> {item.address || '-'}, {item.villageCity ? `${item.villageCity}, ` : ''}{item.district || '-'}, {item.state || '-'}
                      </p>
                    </div>

                    {/* Column 3: Eligibility Details */}
                    <div className="col-md-2 border-end">
                      <h6 className="text-uppercase text-info fw-bold small border-bottom pb-1 mb-2 text-dark">B. Eligibility Details</h6>
                      <p className="mb-1 small"><strong>Category:</strong> {item.category || '-'}</p>
                      <p className="mb-1 small"><strong>Annual Income:</strong> {item.annualFamilyIncome || '-'}</p>
                      <p className="mb-1 small"><strong>Education:</strong> {item.educationalQualification || '-'}</p>
                      <p className="mb-1 small"><strong>Occupation:</strong> {item.presentOccupation || '-'}</p>
                      <p className="mb-0 small text-success fw-semibold"><strong>Monthly Income:</strong> {item.presentMonthlyIncome ? `₹${item.presentMonthlyIncome}` : '-'}</p>
                    </div>

                    {/* Column 4: Requirement & Actions */}
                    <div className="col-md-4 d-flex flex-column justify-content-between">
                      <div>
                        <h6 className="text-uppercase text-warning fw-bold small border-bottom pb-1 mb-2 text-dark">C. Requirement Details</h6>
                        <p className="mb-1 small"><strong>Purpose:</strong> {item.purposeForAssistance || '-'}</p>
                        <p className="mb-1 small"><strong>Business:</strong> {item.proposedBusiness || '-'}</p>
                        <p className="mb-1 small text-danger fw-bold"><strong>Loan Req:</strong> {item.approximateLoanRequirement ? `₹${item.approximateLoanRequirement}` : '-'}</p>
                        
                        <p className="mb-1 small"><strong>Cluster/NGO:</strong> {item.belongsToCluster || '-'}</p>
                        
                        {(item.belongsToCluster === 'Yes' || item.belongsToCluster?.startsWith('Yes')) && (
                          <div className="mx-2 p-1 border-start border-2 border-warning bg-light rounded shadow-xs mb-1">
                            <p className="mb-1 small text-muted text-xs">
                              <strong>↳ Name:</strong> {item.clusterNgoName || 'Pending'}
                            </p>
                            <p className="mb-0 small text-muted text-xs">
                              <strong>↳ Artisans:</strong> {item.totalArtisans !== null && item.totalArtisans !== undefined ? item.totalArtisans : '0'}
                            </p>
                          </div>
                        )}
                        
                        <p className="mb-2 small"><strong>NSFDC Fairs:</strong> {item.interestedInExhibitions || '-'}</p>
                      </div>

                      <div className="d-flex justify-content-end border-top pt-2 mt-2">
                        <button 
                          className="btn btn-danger btn-sm fw-bold px-3 shadow-sm d-flex align-items-center gap-2" 
                          onClick={() => handleDelete(item.id)}
                          title="Delete entry permanently"
                        >
                          <i className="bi bi-trash3-fill"></i> Delete Record
                        </button>
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            ))
          ) : (
            <div className="text-center py-5 bg-white rounded border border-gray-200 text-muted mt-3">
              <i className="bi bi-funnel fs-1 mb-3 d-block text-secondary"></i>
              <h5 className="fw-bold">No Records Match Your Filters</h5>
              <p className="mb-0">Try adjusting your search terms, state, or date range.</p>
              <button className="btn btn-outline-primary mt-3" onClick={resetFilters}>Clear All Filters</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkshopsTable;